import { EllipsisVertical } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/core/components/auth/store/auth";
import { getRoleName } from "@/core/utils/role";
import {
  SIDEBAR_WIDTH_MOBILE,
  SidebarFooter as SidebarFooterUI,
  useSidebar,
} from "@/core/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@radix-ui/react-dropdown-menu";
import { Sheet, SheetContent } from "@/core/components/ui/sheet";
import { DS_COLOR, DS_TYPOGRAPHY } from "@/core/lib/ds-typography";
import { WosUserMenu } from "./wos-user-menu";

/** Avatar circular de 36px (Avatar do DS): foto do usuário ou iniciais. */
function WosUserAvatar() {
  const { userInfo } = useAuth();
  const [imageFailed, setImageFailed] = useState(false);
  const picture = userInfo.user.picture;

  return (
    <span
      aria-hidden
      className="relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border"
      style={{
        width: 36,
        height: 36,
        borderColor: DS_COLOR.strokeDefault,
        backgroundColor: DS_COLOR.surfaceAccentSecondary,
        ...DS_TYPOGRAPHY.labelXsmall,
        color: DS_COLOR.contentDefault,
      }}
    >
      {picture && !imageFailed ? (
        <img
          src={picture}
          alt=""
          className="absolute inset-0 size-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <>
          {userInfo.user.first_name.charAt(0)}
          {userInfo.user.last_name.charAt(0)}
        </>
      )}
    </span>
  );
}

function WosUserCard({ hideText }: { hideText: boolean }) {
  const { userInfo } = useAuth();
  const [translate] = useTranslation(["core"]);

  const roleKey = getRoleName(
    userInfo.profile.role[0] as Parameters<typeof getRoleName>[0],
  );

  return (
    <div className="flex min-w-0 flex-1 items-center gap-4">
      <WosUserAvatar />
      {!hideText && (
        <div className="flex min-w-0 flex-col gap-1">
          <div
            className="truncate"
            style={{
              ...DS_TYPOGRAPHY.labelXsmall,
              color: DS_COLOR.contentDefault,
            }}
          >
            {userInfo.user.first_name} {userInfo.user.last_name}
          </div>
          <div
            className="truncate"
            style={{
              ...DS_TYPOGRAPHY.captionMedium,
              color: DS_COLOR.contentSubtle,
            }}
          >
            {translate(`${roleKey}`)}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "User Menu" da Sidebar do DS (Figma 6051:2744): divisor superior, avatar,
 * nome (Label/Xsmall), cargo (Caption/Medium) e Button Icon "⋮" de 36px que abre
 * o menu do usuário. A empresa passou para o "Entity Menu" do topo.
 */
export function WosSidebarFooter() {
  const [openMobile, setOpenMobile] = useState<boolean>(false);
  const { state, isMobile } = useSidebar();
  const [translate] = useTranslation(["core"]);

  const hideText = state === "collapsed";

  return (
    <SidebarFooterUI
      className={`relative cursor-pointer gap-0 py-3 ${hideText ? "px-1" : "px-4"}`}
      data-testid="wos-sidebar-footer"
    >
      <span
        aria-hidden
        className="absolute top-0 h-px"
        style={{
          left: hideText ? 4 : 16,
          right: hideText ? 4 : 16,
          backgroundColor: DS_COLOR.strokeDefault,
        }}
      />
      <DropdownMenu>
        {isMobile ? (
          <div onClick={() => setOpenMobile(true)}>
            <WosUserCard hideText={hideText} />
          </div>
        ) : (
          <DropdownMenuTrigger asChild>
            <div
              className={`flex items-center gap-4 ${hideText ? "justify-center" : "justify-between"}`}
              aria-label={translate("chatbot-history-open-menu") as string}
            >
              <WosUserCard hideText={hideText} />
              {!hideText && (
                <span
                  className="flex shrink-0 items-center justify-center rounded-full hover:bg-[var(--woba-color-surface-common-hover)]"
                  style={{
                    width: 36,
                    height: 36,
                    color: DS_COLOR.contentSubtle,
                  }}
                >
                  <EllipsisVertical
                    className="shrink-0"
                    style={{ width: 16, height: 16 }}
                  />
                </span>
              )}
            </div>
          </DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          className="min-w-56 rounded-lg"
          side="right"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="px-1.5 py-1">
              <WosUserCard hideText={false} />
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuGroup className="mt-2 flex flex-col">
            <WosUserMenu as="dropdown" />
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={"left"}
        >
          <div className="flex h-full w-full flex-col pt-6">
            <WosUserMenu as="sheet" />
          </div>
        </SheetContent>
      </Sheet>
    </SidebarFooterUI>
  );
}
